import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UserProfile } from '../types/models';

export type SearchLocationTheme = 'rose' | 'orange';

/**
 * Liste/market sorgusu için konum: varsayılan profil; isteğe bağlı geçici il/ilçe override.
 */
export function useSearchLocationFilter(profile: UserProfile | null) {
  const profileCity = profile?.city?.trim() ?? '';
  const profileDistrict = profile?.district?.trim() ?? '';

  const [override, setOverride] = useState<{ city: string; district: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftCity, setDraftCity] = useState('');
  const [draftDistrict, setDraftDistrict] = useState('');

  const effectiveCity = override?.city ?? profileCity;
  const effectiveDistrict = override?.district ?? profileDistrict;
  const canSearch = Boolean(effectiveCity?.trim());

  const isTemporaryFilter = useMemo(() => {
    if (!override) {
      return false;
    }
    return (
      override.city !== profileCity ||
      override.district !== profileDistrict
    );
  }, [override, profileCity, profileDistrict]);

  useEffect(() => {
    if (pickerOpen) {
      setDraftCity(effectiveCity);
      setDraftDistrict(effectiveDistrict);
    }
  }, [pickerOpen, effectiveCity, effectiveDistrict]);

  const openPicker = useCallback(() => {
    setDraftCity(effectiveCity);
    setDraftDistrict(effectiveDistrict);
    setPickerOpen(true);
  }, [effectiveCity, effectiveDistrict]);

  const applyPicker = useCallback(() => {
    const c = draftCity.trim();
    if (!c) {
      setPickerOpen(false);
      return;
    }
    const d = draftDistrict.trim();
    if (c === profileCity && d === profileDistrict) {
      setOverride(null);
    } else {
      setOverride({ city: c, district: d });
    }
    setPickerOpen(false);
  }, [draftCity, draftDistrict, profileCity, profileDistrict]);

  const clearFilter = useCallback(() => {
    setOverride(null);
  }, []);

  return {
    profileCity,
    profileDistrict,
    effectiveCity,
    effectiveDistrict,
    canSearch,
    isTemporaryFilter,
    pickerOpen,
    setPickerOpen,
    draftCity,
    setDraftCity,
    draftDistrict,
    setDraftDistrict,
    openPicker,
    applyPicker,
    clearFilter,
  };
}
